import { useTranslation } from 'react-i18next';
import { Box, Card, Divider, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useAtom, useAtomValue } from 'jotai';
import { Address, Administrative_Area, Business_Profile_Enum, Company, Delivery_Zone, Maybe, Social_Tag } from '@kheops/graphql';
import SizedImage from '../../common/components/SizedImage';
import FileUtils from '../../common/utils/file.utils';
import TrustedSupplierIcon from '../../assets/icons/trusted_icon.svg?react';
import { currentContextAtom } from '../../state';
import { ContractPreview } from '../../hooks/useContracts';
import { DisplayPhoto } from '../../common/common';
import BackButton from '../../common/components/BackButton';
import { RoutePaths } from '../../routes/AppRoutes';
import CompanyLogo from '../../common/components/CompanyLogo';
import { getCompanyShortName } from '../../common/utils/common.utils';
import Breadcrumb from '../../common/components/BreadCrumb';
import { preferredProductFamilyAtom } from '../../common/state/state';
import { CompanyServices } from '../../common/models/user.models';
import CompanySocialTagsSubtitle from '../../social-proof/CompanySocialTagsSubtitle';
import useRelevantSocialTags from '../../social-proof/hooks/useRelevantSocialTags';
import ContactBox from './ContactBox';
import CompanyHeaderChips from './CompanyHeaderChips';

export type CompanyDeliveryZone = Pick<Delivery_Zone, 'minimum_order_value' | 'minimum_order_value_type' | 'negotiable_minimum_order_value' | 'delivery_time'> & {
  administrative_areas: Pick<Administrative_Area, 'area'>[];
}

export type CompanyAddress = Pick<Address, 'administrativeAreaLevel2' | 'formattedAddress' | 'latitude' | 'longitude' | 'locality' | 'postalTown' | 'postalCode' | 'countryCode'>;

export type CompanyInfo = Pick<
  Company,
  'business_profile' | 'contactEmail' | 'contactPhoneNumber' | 'tradeName' | 'description' | 'brand' | 'id' | 'legalName' | 'is_wholesaler'
> & {
  photos?: Maybe<DisplayPhoto[]>;
  addresses: CompanyAddress[];
  delivery_zones: CompanyDeliveryZone[];
  social_tags: Pick<Social_Tag, 'type' | 'priority' | 'level' | 'values'>[];
} & CompanyServices;

export interface CompanyInfoCardProps {
  company: CompanyInfo;
  contract: ContractPreview;
}

export function CompanyInfoCard({ company, contract }: CompanyInfoCardProps): React.JSX.Element {
  const theme = useTheme();
  const isSmallerThanLg = useMediaQuery(theme.breakpoints.down('lg'));
  const { t } = useTranslation(['common', 'contracts', 'discovery', 'catalogs']);
  const context = useAtomValue(currentContextAtom);
  const [, setPreferredProductFamilies] = useAtom(preferredProductFamilyAtom);

  const socialTags = useRelevantSocialTags(
    company.social_tags,
    { rules: { 1: 1, 2: 1 } },
  );

  const backTo = RoutePaths.CONTRACT_LIST;

  return (
    <>
      {!isSmallerThanLg && (
        <>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
            }}
          >
            <BackButton
              to={backTo}
              asLink
            />
            <Divider
              orientation="vertical"
              sx={{
                height: '8px',
                mx: '8px',
              }}
            />
            <Breadcrumb
              items={[
                {
                  label: t(context.realm === Business_Profile_Enum.Buyer ? 'discovery:all_suppliers' : 'catalogs:all_stores'),
                  to: backTo,
                  beforeNavigate: () => { setPreferredProductFamilies([]); },
                },
                {
                  label: getCompanyShortName(company.tradeName, company.business_profile),
                },
              ]}
            />
          </Box>
          <Card
            sx={{
              display: 'flex',
              p: { lg: 1.5 },
              backgroundColor: 'surfaceContainer.main',
              flexDirection: 'row',
              justifyContent: 'normal',
              mb: 2,
              mt: 1.5,
              borderRadius: 25,
              alignItems: 'center',
            }}
          >
            <SizedImage
              src={FileUtils.BuildCompanyImageUrl({
                photo: company.photos ? company.photos[0]?.photo : undefined,
                type: company.business_profile,
                size: { height: 80, width: 80 },
              })}
              alt={t('common:company')}
              height={80}
              width={80}
              borderRadius={25}
              sx={{
                mr: 1.5,
                overflow: 'visible',
                '& img': {
                  borderRadius: 25,
                },
              }}
              overlay={(
                // eslint-disable-next-line react/jsx-no-useless-fragment
                <>
                  {company.brand && (
                    <CompanyLogo
                      brand={company.brand}
                      size="medium"
                      variant="circle"
                      sx={{
                        position: 'absolute',
                        bottom: {
                          lg: 0,
                        },
                        right: {
                          lg: 0,
                        },
                        width: {
                          lg: 40,
                        },
                      }}
                    />
                  )}
                </>
              )}
            />
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                width: '100%',
                m: 0,
              }}
            >
              <Box sx={{ mt: 1 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {context.realm === Business_Profile_Enum.Buyer && contract.is_trusted_supplier && (
                    <TrustedSupplierIcon width={26} height={26} />
                    )}
                    <Typography variant="titleLarge">
                      {getCompanyShortName(company.tradeName, company.business_profile)}
                    </Typography>
                  </Box>
                  {context.realm === Business_Profile_Enum.Buyer && (
                    <Typography variant="bodySmall">
                      <CompanySocialTagsSubtitle
                        isWholesaler={company.is_wholesaler}
                        tags={socialTags}
                        geoloc={{
                          lat: company.addresses[0].latitude,
                          lng: company.addresses[0].longitude,
                        }}
                      />
                    </Typography>
                  )}
                  <Box sx={{ mt: 0.5 }}>
                    <CompanyHeaderChips company={company} contract={contract} />
                  </Box>
                </Box>
              </Box>
              <ContactBox contactCompanyId={company.id} contract={contract} />
            </Box>
          </Card>
        </>
      )}

      {isSmallerThanLg && (
        <Box sx={{ my: 0.5 }}>
          <CompanyHeaderChips company={company} contract={contract} compact />
        </Box>
      )}
    </>
  );
}
